import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSecureAuth } from './SecureAuthContext';
import { apiService } from '../config/api';
import { analyticsService } from '../services/analyticsService';

interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'master' | 'researcher' | 'participant';
  status?: 'noprofile' | 'pending' | 'approved' | 'rejected';
  institution?: string;
  department?: string;
  position?: string;
  approvedAt?: string;
  isMasterAccount?: boolean;
}

interface UserContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  isMasterAccount: boolean;
  isApprovedResearcher: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: currentUser, isLoading: authLoading } = useSecureAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUserProfile = async () => {
    // If auth is still loading, keep user loading as true
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!currentUser) {
      setUserProfile(null);
      setLoading(false);
      return;
    }

    // Set loading to true when we start loading for an authenticated user
    setLoading(true);
    
    try {
      setError(null);

      // First check if user is a master account
      // Get token from secure auth service
      const token = await apiService.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      try {
        const masterResponse = await apiService.get('/api/admin/check-master-status');
        
        if (masterResponse.success && masterResponse.isMasterAccount) {
          setUserProfile({
            id: 'master',
            email: currentUser.email || '',
            firstName: currentUser.displayName?.split(' ')[0] || '',
            lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
            role: 'master',
            isMasterAccount: true
          });
          analyticsService.identifyUser('master', {
            role: 'master',
            isMasterAccount: true
          });
          return;
        }
      } catch (masterError: any) {
        // Master account check failed, continue to researcher profile check
      }

      // If not master, check researcher profile
      let researcherResponse;
      try {
        researcherResponse = await apiService.get('/api/auth/profile');
      } catch (apiError: any) {
        // 404 or other error means no profile - this is expected for new users
        // Check if it's a 404 specifically (profile not found)
        const is404 = apiError.message?.includes('404') || apiError.message?.includes('not found');
        
        if (is404) {
          // Expected: User has no profile yet - set as noprofile
          setUserProfile({
            id: currentUser.uid,
            email: currentUser.email || '',
            firstName: currentUser.displayName?.split(' ')[0] || '',
            lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
            role: 'researcher',
            status: 'noprofile',
            isMasterAccount: false
          });
          setLoading(false);
          return;
        } else {
          // Unexpected error - rethrow to outer catch
          throw apiError;
        }
      }
      
      if (researcherResponse.success && researcherResponse.researcher) {
        const researcher = researcherResponse.researcher;
        setUserProfile({
          id: researcher.id,
          email: researcher.email,
          firstName: researcher.firstName,
          lastName: researcher.lastName,
          role: 'researcher',
          status: researcher.status || 'noprofile',
          institution: researcher.institution,
          department: researcher.department,
          position: researcher.position,
          approvedAt: researcher.approvedAt,
          isMasterAccount: false
        });
        analyticsService.identifyUser(researcher.id, {
          role: 'researcher',
          status: researcher.status || 'noprofile',
          institution: researcher.institution,
          isMasterAccount: false
        });
      } else {
        // API returned success: false but no error thrown - set as noprofile
        setUserProfile({
          id: currentUser.uid,
          email: currentUser.email || '',
          firstName: currentUser.displayName?.split(' ')[0] || '',
          lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || '',
          role: 'researcher',
          status: 'noprofile',
          isMasterAccount: false
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load user profile');
      
      // Fallback profile for authenticated users - set as noprofile
      setUserProfile({
        id: currentUser.uid,
        email: currentUser.email || '',
        role: 'researcher',
        status: 'noprofile',
        isMasterAccount: false
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    await loadUserProfile();
  };

  useEffect(() => {
    loadUserProfile();
  }, [currentUser, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMasterAccount = !loading && (userProfile?.role === 'master' || userProfile?.isMasterAccount === true);
  const isApprovedResearcher = !loading && userProfile?.role === 'researcher' && userProfile?.status === 'approved';

  const value: UserContextType = {
    userProfile,
    loading,
    error,
    refreshProfile,
    isMasterAccount,
    isApprovedResearcher,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
